import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

// Focus ring omitted: app-wide `:focus-visible` outline in focus.css is the
// single focus treatment. Density-aware heights resolve from --density-control-*
// (see density.css). Default = 36px / Figma control-radius/md (Normal 12px).
const buttonVariants = cva(
  "group/button inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-hidden select-none disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive [&_[data-slot=icon],&_svg]:pointer-events-none [&_[data-slot=icon],&_svg]:shrink-0 [&_[data-slot=icon]:not([class*='size-']),&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        // Near-black primary CTA (Save / Create / Assign). Blue owns selection/focus.
        dark: "bg-foreground text-background hover:bg-foreground/90",
        outline:
          "border-border bg-background hover:bg-accent hover:text-accent-foreground aria-expanded:bg-accent aria-expanded:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-accent hover:text-accent-foreground aria-expanded:bg-accent aria-expanded:text-accent-foreground dark:hover:bg-accent/50",
        // Solid destructive (prototype). Soft tint kept as `danger` below for CDS compat.
        destructive: "bg-destructive text-primary-foreground hover:bg-destructive/90",
        link: "text-primary underline-offset-4 hover:underline",
        danger: "bg-destructive text-primary-foreground hover:bg-destructive/90",
        info: "bg-info text-primary-foreground hover:bg-info/90",
        success: "bg-success text-primary-foreground hover:bg-success/90",
        warning: "bg-warning text-primary-foreground hover:bg-warning/90",
        // Solid caution (yellow). On-fill text stays dark — yellow-9 is bright in both modes.
        caution: "bg-caution text-caution-foreground hover:bg-caution/90",
        // Bordered toolbar control — open/active via aria-expanded → blue chrome.
        toolbar:
          "border border-border bg-card text-foreground shadow-sm hover:bg-accent aria-expanded:border-primary aria-expanded:bg-selected aria-expanded:text-selected-foreground aria-expanded:hover:bg-selected",
      },
      size: {
        default: "h-(--density-control-h) px-(--density-control-px) has-[>[data-slot=icon],>svg]:px-2",
        xs: "h-(--density-control-h-xs) gap-1 rounded-sm px-2 text-xs has-[>[data-slot=icon],>svg]:px-1.5 [&_[data-slot=icon]:not([class*='size-']),&_svg:not([class*='size-'])]:size-3",
        sm: "h-(--density-control-h-sm) gap-1 rounded-sm px-(--density-control-px) text-[0.8rem] has-[>[data-slot=icon],>svg]:px-2 [&_[data-slot=icon]:not([class*='size-']),&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-(--density-control-h-lg) px-(--density-control-px)",
        icon: "size-(--density-control-h)",
        "icon-xs": "size-(--density-control-h-xs) rounded-sm [&_[data-slot=icon]:not([class*='size-']),&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-(--density-control-h-sm) rounded-sm",
        "icon-lg": "size-(--density-control-h-lg)",
        toolbar: "h-(--density-control-h) px-(--density-control-px)",
        "toolbar-icon": "size-(--density-control-h)",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = ({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) => {
  return (
    <ButtonPrimitive
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
};

export { Button, buttonVariants };
